import { useState, useEffect, useRef } from 'react'
import {
	Box,
	Typography,
	Button,
	IconButton,
	Popover,
	Slider,
	TextField
} from '@mui/material'
import {
	Add as AddIcon,
	Remove as RemoveIcon,
	Close as CloseIcon
} from '@mui/icons-material'
import {
	calculateProportionalDurations,
	getAppointmentDuration,
	MIN_DURATION_INTERVAL
} from '../utils/appointmentUtils'

/**
 * DurationChangePopUp - Popup for adjusting total appointment duration
 * 
 * @param {HTMLElement} anchorEl - The anchor element for the popover
 * @param {boolean} open - Whether the popover is open
 * @param {function} onClose - Callback to close the popover
 * @param {Array} appointments - Array of appointment objects
 * @param {function} onSave - Callback with new durations: (newDurations) => void
 * @param {boolean} isMobile - Mobile view flag
 */
export default function DurationChangePopUp({
	anchorEl,
	open,
	onClose,
	appointments = [],
	onSave,
	isMobile = false
}) {
	// Calculate current total duration
	const currentTotalDuration = appointments.reduce((sum, apt) => {
		return sum + getAppointmentDuration(apt)
	}, 0)

	const [newTotalDuration, setNewTotalDuration] = useState(currentTotalDuration)
	const [previewDurations, setPreviewDurations] = useState([])
	const [inputValue, setInputValue] = useState(String(currentTotalDuration))
	const inputRef = useRef(null)

	// Reset duration when appointments change or popup opens
	useEffect(() => {
		if (open) {
			setNewTotalDuration(currentTotalDuration)
			setInputValue(String(currentTotalDuration))
		}
	}, [open, currentTotalDuration])

	// Sync input value when duration changes from slider or buttons
	useEffect(() => {
		setInputValue(String(newTotalDuration))
	}, [newTotalDuration])

	// Calculate preview whenever duration changes
	useEffect(() => {
		if (appointments.length === 0) return

		const appointmentsWithDuration = appointments.map(apt => ({
			...apt,
			duration: getAppointmentDuration(apt)
		}))

		const newDurations = calculateProportionalDurations(
			appointmentsWithDuration,
			newTotalDuration
		)

		setPreviewDurations(newDurations)
	}, [appointments, newTotalDuration])

	// Handle increment/decrement
	const handleIncrement = () => {
		setNewTotalDuration(prev => prev + MIN_DURATION_INTERVAL)
	}

	const handleDecrement = () => {
		// Minimum total duration is MIN_DURATION_INTERVAL * number of appointments
		const minDuration = MIN_DURATION_INTERVAL * appointments.length
		setNewTotalDuration(prev => Math.max(minDuration, prev - MIN_DURATION_INTERVAL))
	}

	// Handle slider change
	const handleSliderChange = (_, value) => {
		setNewTotalDuration(value)
	}

	// Handle manual input change - only allow integers
	const handleInputChange = (e) => {
		const value = e.target.value
		// Only allow digits
		if (value === '' || /^\d+$/.test(value)) {
			setInputValue(value)
		}
	}

	// Handle input blur - round to nearest 5 minutes and apply constraints
	const handleInputBlur = () => {
		let value = parseInt(inputValue, 10)

		// If empty or invalid, reset to current duration
		if (isNaN(value) || value <= 0) {
			setInputValue(String(newTotalDuration))
			return
		}

		// Round to nearest 5 minutes
		value = Math.round(value / MIN_DURATION_INTERVAL) * MIN_DURATION_INTERVAL

		// Apply min/max constraints
		value = Math.max(minDuration, Math.min(maxDuration, value))

		setNewTotalDuration(value)
		setInputValue(String(value))
	}

	// Handle Enter key to apply the input
	const handleInputKeyDown = (e) => {
		if (e.key === 'Enter') {
			handleInputBlur()
			inputRef.current?.blur()
		}
	}

	// Handle save
	const handleSave = () => {
		if (onSave && previewDurations.length > 0) {
			onSave(previewDurations)
		}
		onClose()
	}

	// Calculate min and max for slider
	const minDuration = MIN_DURATION_INTERVAL * appointments.length
	const maxDuration = Math.max(currentTotalDuration * 2, 180) // Max 2x current or 3 hours

	// Format duration for display
	const formatDuration = (minutes) => {
		if (minutes >= 60) {
			const hours = Math.floor(minutes / 60)
			const mins = minutes % 60
			return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
		}
		return `${minutes}m`
	}

	return (
		<Popover
			open={open}
			anchorEl={anchorEl}
			onClose={onClose}
			anchorOrigin={{
				vertical: 'top',
				horizontal: 'center'
			}}
			transformOrigin={{
				vertical: 'bottom',
				horizontal: 'center'
			}}
			PaperProps={{
				sx: {
					width: isMobile ? '90vw' : '320px',
					maxWidth: '320px',
					borderRadius: '12px',
					boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
					overflow: 'hidden'
				}
			}}
		>
			{/* Header */}
			<Box sx={{
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'center',
				p: '12px 16px',
				borderBottom: '1px solid #E2E8F0'
			}}>
				<Typography sx={{
					fontFamily: 'Inter, sans-serif',
					fontWeight: 600,
					fontSize: '16px',
					color: '#2D3748'
				}}>
					Adjust Duration
				</Typography>
				<IconButton
					onClick={onClose}
					size="small"
					sx={{ p: '4px' }}
				>
					<CloseIcon sx={{ fontSize: 20, color: '#718096' }} />
				</IconButton>
			</Box>

			{/* Content */}
			<Box sx={{ p: '16px' }}>
				{/* Duration Adjuster */}
				<Box sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					gap: '16px',
					mb: '16px'
				}}>
					<IconButton
						onClick={handleDecrement}
						disabled={newTotalDuration <= minDuration}
						sx={{
							bgcolor: '#F7FAFC',
							border: '1px solid #E2E8F0',
							borderRadius: '8px',
							width: '40px',
							height: '40px',
							'&:hover': {
								bgcolor: '#EDF2F7'
							},
							'&:disabled': {
								bgcolor: '#F7FAFC',
								opacity: 0.5
							}
						}}
					>
						<RemoveIcon sx={{ color: '#9F7AEA' }} />
					</IconButton>

					<Box sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						minWidth: '100px'
					}}>
						<TextField
							inputRef={inputRef}
							value={inputValue}
							onChange={handleInputChange}
							onBlur={handleInputBlur}
							onKeyDown={handleInputKeyDown}
							variant="standard"
							inputProps={{
								inputMode: 'numeric',
								pattern: '[0-9]*',
								style: {
									fontFamily: 'Inter, sans-serif',
									fontWeight: 700,
									fontSize: '32px',
									color: '#2D3748',
									lineHeight: 1,
									textAlign: 'center',
									width: '80px'
								}
							}}
							InputProps={{
								disableUnderline: true
							}}
							sx={{
								'& .MuiInputBase-root': {
									'&:hover': {
										bgcolor: 'rgba(159, 122, 234, 0.08)',
										borderRadius: '8px'
									}
								},
								'& .MuiInputBase-input': {
									padding: '4px 8px',
									borderRadius: '8px',
									'&:focus': {
										bgcolor: 'rgba(159, 122, 234, 0.12)',
										outline: '2px solid #9F7AEA'
									}
								}
							}}
						/>
						<Typography sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: '14px',
							color: '#718096'
						}}>
							minutes
						</Typography>
					</Box>

					<IconButton
						onClick={handleIncrement}
						disabled={newTotalDuration >= maxDuration}
						sx={{
							bgcolor: '#F7FAFC',
							border: '1px solid #E2E8F0',
							borderRadius: '8px',
							width: '40px',
							height: '40px',
							'&:hover': {
								bgcolor: '#EDF2F7'
							},
							'&:disabled': {
								bgcolor: '#F7FAFC',
								opacity: 0.5
							}
						}}
					>
						<AddIcon sx={{ color: '#9F7AEA' }} />
					</IconButton>
				</Box>

				{/* Slider */}
				<Box sx={{ px: '8px', mb: '16px' }}>
					<Slider
						value={newTotalDuration}
						onChange={handleSliderChange}
						min={minDuration}
						max={maxDuration}
						step={MIN_DURATION_INTERVAL}
						sx={{
							color: '#9F7AEA',
							'& .MuiSlider-thumb': {
								bgcolor: '#9F7AEA',
								'&:hover': {
									boxShadow: '0 0 0 8px rgba(159, 122, 234, 0.16)'
								}
							},
							'& .MuiSlider-track': {
								bgcolor: '#9F7AEA'
							},
							'& .MuiSlider-rail': {
								bgcolor: '#E2E8F0'
							}
						}}
					/>
					<Box sx={{
						display: 'flex',
						justifyContent: 'space-between'
					}}>
						<Typography sx={{ fontSize: '12px', color: '#A0AEC0' }}>
							{formatDuration(minDuration)}
						</Typography>
						<Typography sx={{ fontSize: '12px', color: '#A0AEC0' }}>
							{formatDuration(maxDuration)}
						</Typography>
					</Box>
				</Box>

				{/* Duration Breakdown Preview */}
				{appointments.length > 1 && previewDurations.length > 0 && (
					<Box sx={{
						bgcolor: '#F7FAFC',
						borderRadius: '8px',
						p: '12px',
						mb: '16px'
					}}>
						<Typography sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: '12px',
							color: '#718096',
							mb: '8px',
							textTransform: 'uppercase',
							letterSpacing: '0.5px'
						}}>
							Duration Breakdown
						</Typography>
						{previewDurations.map((item, idx) => {
							const apt = appointments[item.index]
							const petName = apt?.pet?.name || `Pet ${idx + 1}`
							const serviceName = apt?.service?.name || 'Service'
							const changed = item.newDuration !== item.originalDuration

							return (
								<Box
									key={idx}
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										py: '6px',
										borderBottom: idx < previewDurations.length - 1 ? '1px solid #E2E8F0' : 'none'
									}}
								>
									<Box sx={{ flex: 1, minWidth: 0 }}>
										<Typography sx={{
											fontFamily: 'Inter, sans-serif',
											fontWeight: 500,
											fontSize: '13px',
											color: '#2D3748',
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap'
										}}>
											{petName}
										</Typography>
										<Typography sx={{
											fontFamily: 'Inter, sans-serif',
											fontWeight: 400,
											fontSize: '11px',
											color: '#A0AEC0',
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap'
										}}>
											{serviceName}
										</Typography>
									</Box>
									<Box sx={{
										display: 'flex',
										alignItems: 'center',
										gap: '4px'
									}}>
										{changed && (
											<Typography sx={{
												fontFamily: 'Inter, sans-serif',
												fontWeight: 400,
												fontSize: '12px',
												color: '#A0AEC0',
												textDecoration: 'line-through'
											}}>
												{item.originalDuration}m
											</Typography>
										)}
										<Typography sx={{
											fontFamily: 'Inter, sans-serif',
											fontWeight: 600,
											fontSize: '13px',
											color: changed ? '#9F7AEA' : '#2D3748'
										}}>
											{item.newDuration}m
										</Typography>
									</Box>
								</Box>
							)
						})}
					</Box>
				)}

				{/* Single appointment preview */}
				{appointments.length === 1 && previewDurations.length > 0 && (
					<Box sx={{
						bgcolor: '#F7FAFC',
						borderRadius: '8px',
						p: '12px',
						mb: '16px'
					}}>
						<Box sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center'
						}}>
							<Typography sx={{
								fontFamily: 'Inter, sans-serif',
								fontWeight: 500,
								fontSize: '13px',
								color: '#2D3748'
							}}>
								{appointments[0]?.service?.name || 'Service'}
							</Typography>
							<Box sx={{
								display: 'flex',
								alignItems: 'center',
								gap: '4px'
							}}>
								{previewDurations[0].newDuration !== previewDurations[0].originalDuration && (
									<Typography sx={{
										fontFamily: 'Inter, sans-serif',
										fontWeight: 400,
										fontSize: '12px',
										color: '#A0AEC0',
										textDecoration: 'line-through'
									}}>
										{previewDurations[0].originalDuration}m
									</Typography>
								)}
								<Typography sx={{
									fontFamily: 'Inter, sans-serif',
									fontWeight: 600,
									fontSize: '13px',
									color: previewDurations[0].newDuration !== previewDurations[0].originalDuration ? '#9F7AEA' : '#2D3748'
								}}>
									{previewDurations[0].newDuration}m
								</Typography>
							</Box>
						</Box>
					</Box>
				)}

				{/* Change indicator */}
				{newTotalDuration !== currentTotalDuration && (
					<Box sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						gap: '8px',
						mb: '16px'
					}}>
						<Typography sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: '13px',
							color: '#718096'
						}}>
							{currentTotalDuration}m
						</Typography>
						<Typography sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: '13px',
							color: '#718096'
						}}>
							→
						</Typography>
						<Typography sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: '13px',
							color: '#9F7AEA'
						}}>
							{newTotalDuration}m
						</Typography>
						<Typography sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: '12px',
							color: newTotalDuration > currentTotalDuration ? '#48BB78' : '#F56565'
						}}>
							({newTotalDuration > currentTotalDuration ? '+' : ''}{newTotalDuration - currentTotalDuration}m)
						</Typography>
					</Box>
				)}
			</Box>

			{/* Actions */}
			<Box sx={{
				display: 'flex',
				gap: '10px',
				p: '12px 16px',
				borderTop: '1px solid #E2E8F0'
			}}>
				<Button
					onClick={onClose}
					variant="outlined"
					sx={{
						flex: 1,
						p: '10px 16px',
						color: '#9F7AEA',
						borderColor: '#CBD5E0',
						borderRadius: '6px',
						fontFamily: 'Inter, sans-serif',
						fontWeight: 600,
						fontSize: '14px',
						textTransform: 'none',
						'&:hover': {
							borderColor: '#9F7AEA',
							bgcolor: 'transparent'
						}
					}}
				>
					Cancel
				</Button>
				<Button
					onClick={handleSave}
					variant="contained"
					disabled={newTotalDuration === currentTotalDuration}
					sx={{
						flex: 1,
						p: '10px 16px',
						bgcolor: '#E9D8FD',
						color: '#2D3748',
						borderRadius: '6px',
						border: '1px solid #9F7AEA',
						fontFamily: 'Inter, sans-serif',
						fontWeight: 600,
						fontSize: '14px',
						textTransform: 'none',
						boxShadow: 'none',
						'&:hover': {
							bgcolor: '#D6BCFA',
							boxShadow: 'none'
						},
						'&:disabled': {
							bgcolor: '#E2E8F0',
							color: '#A0AEC0',
							border: '1px solid #CBD5E0'
						}
					}}
				>
					Apply
				</Button>
			</Box>
		</Popover>
	)
}
